import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Table, Dialog, Input, Textarea, Alert, Badge, useToast } from '@dreamtree-org/twreact-ui';
import { Check, X, Percent } from 'lucide-react';
import { PageHeader, QueryState, EmptyState, SectionCard, StatusBadge, KeyValue, StatCard } from '@/components';
import { errorText, formatINR, formatDate } from '@/lib/format';
import { useQuery, useMutation } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import StatusTabs from '../components/StatusTabs';
import { PRODUCT_TABS, VENDOR_ACTIONS, actionsFor, pctLabel, parsePct, effectivePct, groupByCategory } from '../lib/vendors';

/**
 * One vendor (B17): its profile and commission, then every product it listed,
 * grouped by category › subcategory. Approve a product to put it on the
 * storefront, reject it with a reason, or give it its own commission.
 */
export default function VendorDetail() {
  const { id } = useParams();
  const can = useCan();
  const { toast } = useToast();
  const vendorId = Number(id);
  const list = useQuery('mktVendors', {}, { select: (r) => r.data });
  const [tab, setTab] = useState('pending');
  const products = useQuery('mktProducts', { store_vendor_id: vendorId, ...(tab ? { review_status: tab } : {}) }, { select: (r) => r.data || [] });
  const earnings = useQuery('mktEarnings', undefined, { select: (r) => (r.data || []).find((e) => e.vendor.id === vendorId) || null });
  const reviewVendorM = useMutation('mktReviewVendor');
  const vendorPctM = useMutation('mktSetVendorCommission');
  const reviewM = useMutation('mktReviewProduct');
  const productPctM = useMutation('mktSetProductCommission');

  const [acting, setActing] = useState(null); // vendor action { action, note }
  const [vendorPct, setVendorPct] = useState(null); // string
  const [rejecting, setRejecting] = useState(null); // { product, note }
  const [productPct, setProductPct] = useState(null); // { product, value }
  const [error, setError] = useState('');

  const vendor = list.data ? list.data.vendors.find((v) => v.id === vendorId) : undefined;
  const storePct = list.data ? list.data.default_commission_pct : null;

  const guarded = async (fn) => {
    setError('');
    try {
      await fn();
      return true;
    } catch (ex) {
      setError(errorText(ex));
      return false;
    }
  };

  const approveProduct = (p) => guarded(async () => {
    const r = await reviewM.run({ id: p.id, decision: 'approve' });
    toast.success(r.data && r.data.live ? 'Approved — now on your storefront' : 'Approved', p.title);
    products.reload();
  });
  const rejectProduct = () => guarded(async () => {
    await reviewM.run({ id: rejecting.product.id, decision: 'reject', note: rejecting.note });
    toast.success('Product rejected', rejecting.product.title);
    setRejecting(null);
    products.reload();
  });
  const saveProductPct = () => {
    const c = parsePct(productPct.value);
    if (c.error) return setError(c.error);
    return guarded(async () => {
      await productPctM.run({ id: productPct.product.id, commission_pct: c.value });
      toast.success('Commission saved', `${productPct.product.title}: ${pctLabel(c.value, effectivePct(null, vendor && vendor.commission_pct, storePct))}`);
      setProductPct(null);
      products.reload();
    });
  };
  const saveVendorPct = () => {
    const c = parsePct(vendorPct);
    if (c.error) return setError(c.error);
    return guarded(async () => {
      await vendorPctM.run({ id: vendor.id, commission_pct: c.value, version: vendor.version });
      toast.success('Vendor commission saved', pctLabel(c.value, storePct));
      setVendorPct(null);
      list.reload();
    });
  };
  const runVendorAction = () => guarded(async () => {
    await reviewVendorM.run({ action: acting.action, id: vendor.id, note: acting.note || undefined, version: vendor.version });
    toast.success(VENDOR_ACTIONS[acting.action].done, vendor.business_name);
    setActing(null);
    list.reload();
    products.reload();
  });

  const columns = [
    {
      key: 'title', label: 'Product',
      render: (p) => (
        <span>
          <span className="block font-medium">{p.title}</span>
          <span className="block text-xs opacity-70">{p.brand} · {p.sku} · {p.condition.replace(/_/g, ' ')}</span>
          {p.review_note && p.review_status === 'rejected' ? <span className="block text-xs text-danger-700">Rejected: {p.review_note}</span> : null}
        </span>
      ),
    },
    { key: 'price_paise', label: 'Price', render: (p) => <span>{formatINR(p.price_paise)}{p.mrp_paise > p.price_paise ? <span className="block text-xs line-through opacity-50">{formatINR(p.mrp_paise)}</span> : null}</span> },
    { key: 'qty_available', label: 'Stock', render: (p) => `${p.qty_available} / ${p.qty_on_hand}`, hideOnMobile: true },
    { key: 'review_status', label: 'Review', render: (p) => <span className="flex flex-wrap gap-1"><StatusBadge status={p.review_status} />{p.live ? <Badge size="xs" variant="success">Live</Badge> : null}</span> },
    { key: 'commission_pct', label: 'Commission', render: (p) => (p.commission_pct != null ? `${p.commission_pct}%` : <span className="opacity-70">{pctLabel(null, effectivePct(null, vendor && vendor.commission_pct, storePct))}</span>) },
  ];
  const rowActions = (p) => [
    can('marketplace', 'approve') && p.review_status !== 'approved' && { name: 'approve', label: 'Approve', icon: <Check size={14} />, inline: true, onClick: () => approveProduct(p) },
    can('marketplace', 'approve') && p.review_status === 'pending' && { name: 'reject', label: 'Reject', icon: <X size={14} />, onClick: () => { setError(''); setRejecting({ product: p, note: '' }); } },
    can('marketplace', 'update') && { name: 'commission', label: 'Commission', icon: <Percent size={14} />, onClick: () => { setError(''); setProductPct({ product: p, value: p.commission_pct ?? '' }); } },
  ].filter(Boolean);

  return (
    <QueryState query={list}>
      {() => (!vendor ? (
        <EmptyState title="Vendor not found">It may have been removed. Go back to the vendor list.</EmptyState>
      ) : (
        <div className="space-y-6">
          <PageHeader
            title={vendor.business_name}
            subtitle={`${vendor.number} · ${vendor.source === 'added' ? 'Added by the store' : 'Applied'} ${formatDate(vendor.created_at)}`}
            back={{ href: '/vendors', label: 'Vendors' }}
            actions={can('marketplace', 'approve') ? (
              <div className="flex flex-wrap gap-2">
                {actionsFor(vendor.status).map((a) => (
                  <Button key={a.key} variant={a.tone === 'danger' ? 'outline' : 'primary'} onClick={() => { setError(''); setActing({ action: a.key, note: '' }); }}>{a.label}</Button>
                ))}
              </div>
            ) : null}
          />
          {error && !acting && !rejecting && !productPct && vendorPct == null ? <Alert variant="error" title="Not saved" dismissible onDismiss={() => setError('')}>{error}</Alert> : null}
          {vendor.status !== 'approved' ? (
            <Alert variant="warning" title={`This vendor is ${vendor.status}`}>
              {vendor.status === 'pending' ? 'Approve the vendor before approving its products.' : 'Its products are not on your storefront.'}
              {vendor.review_note ? ` Note: ${vendor.review_note}` : ''}
            </Alert>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-4">
            <StatCard label="Status" value={<StatusBadge status={vendor.status} />} />
            <StatCard label="Commission" value={pctLabel(vendor.commission_pct, storePct)} />
            <StatCard label="Sales" value={formatINR(earnings.data ? earnings.data.sales_paise : 0)} hint={earnings.data ? `${earnings.data.lines} items` : 'No sales yet'} />
            <StatCard label="Your commission" value={formatINR(earnings.data ? earnings.data.commission_paise : 0)} hint={earnings.data ? `Vendor share ${formatINR(earnings.data.earning_paise)}` : undefined} />
          </div>

          <SectionCard
            title="Profile"
            actions={can('marketplace', 'update') ? <Button size="sm" variant="outline" leftIcon={<Percent size={14} />} onClick={() => { setError(''); setVendorPct(vendor.commission_pct == null ? '' : String(vendor.commission_pct)); }}>Set commission</Button> : null}
          >
            <KeyValue
              items={[
                { label: 'Contact', value: vendor.contact_name },
                { label: 'Email', value: vendor.email },
                { label: 'Phone', value: vendor.phone || '—' },
                { label: 'GSTIN', value: vendor.gstin || '—' },
                { label: 'Last sign-in', value: vendor.last_login_at ? formatDate(vendor.last_login_at) : 'Never' },
              ]}
            />
          </SectionCard>

          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Products</h2>
            <StatusTabs tabs={PRODUCT_TABS} value={tab} onChange={setTab} />
            <QueryState query={products} skeleton="table">
              {(rows) => (rows && rows.length ? (
                <div className="space-y-5">
                  {groupByCategory(rows).map((g) => (
                    <SectionCard key={g.name} title={`${g.name} (${g.items.length})`}>
                      <Table data={g.items} columns={columns} actions={rowActions} replaceDefaultActions withAction showSerial={false} showColumnPicker={false} showToolbar={false} />
                    </SectionCard>
                  ))}
                </div>
              ) : (
                <EmptyState title={tab === 'pending' ? 'Nothing to review' : 'No products here'}>Products this vendor lists from its portal show up here for review.</EmptyState>
              ))}
            </QueryState>
          </div>

          <Dialog isOpen={!!acting} onClose={() => setActing(null)} title={acting ? `${VENDOR_ACTIONS[acting.action].label} ${vendor.business_name}?` : ''} size="sm">
            {acting ? (
              <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); runVendorAction(); }}>
                <Textarea label={VENDOR_ACTIONS[acting.action].note ? 'Reason (the vendor sees this)' : 'Note (optional)'} value={acting.note} onChange={(e) => setActing({ ...acting, note: e.target.value })} rows={3} maxLength={500} />
                {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setActing(null)}>Cancel</Button>
                  <Button type="submit" variant={VENDOR_ACTIONS[acting.action].tone === 'danger' ? 'destructive' : 'primary'} loading={reviewVendorM.loading} disabled={VENDOR_ACTIONS[acting.action].note && !acting.note.trim()}>{VENDOR_ACTIONS[acting.action].label}</Button>
                </div>
              </form>
            ) : null}
          </Dialog>

          <Dialog isOpen={!!rejecting} onClose={() => setRejecting(null)} title="Reject this product?" size="sm">
            {rejecting ? (
              <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); rejectProduct(); }}>
                <p className="text-sm"><span className="font-medium">{rejecting.product.title}</span> stays off the storefront. The vendor sees your reason and can edit and resubmit.</p>
                <Textarea label="Reason" value={rejecting.note} onChange={(e) => setRejecting({ ...rejecting, note: e.target.value })} rows={3} maxLength={500} required />
                {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setRejecting(null)}>Cancel</Button>
                  <Button type="submit" variant="destructive" loading={reviewM.loading} disabled={!rejecting.note.trim()}>Reject</Button>
                </div>
              </form>
            ) : null}
          </Dialog>

          <Dialog isOpen={!!productPct} onClose={() => setProductPct(null)} title="Product commission" size="sm">
            {productPct ? (
              <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); saveProductPct(); }}>
                <Input label={`Commission on ${productPct.product.title} (%)`} inputMode="decimal" value={productPct.value} onChange={(e) => setProductPct({ ...productPct, value: e.target.value })} helperText={`Empty uses the vendor's rate (${pctLabel(vendor.commission_pct, storePct)}). Applies to new orders only.`} />
                {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setProductPct(null)}>Cancel</Button>
                  <Button type="submit" loading={productPctM.loading}>Save</Button>
                </div>
              </form>
            ) : null}
          </Dialog>

          <Dialog isOpen={vendorPct != null} onClose={() => setVendorPct(null)} title="Vendor commission" size="sm">
            {vendorPct != null ? (
              <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); saveVendorPct(); }}>
                <Input label="Commission (%)" inputMode="decimal" value={vendorPct} onChange={(e) => setVendorPct(e.target.value)} helperText={`Used for this vendor's products without their own rate. Empty uses the store default (${pctLabel(storePct)}).`} />
                {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setVendorPct(null)}>Cancel</Button>
                  <Button type="submit" loading={vendorPctM.loading}>Save</Button>
                </div>
              </form>
            ) : null}
          </Dialog>
        </div>
      ))}
    </QueryState>
  );
}
